import type { PlacedElements } from "../blocks/saved";
import { layoutGridGeometry } from "./layout";
import type { ImageMask } from "./masks";
import { createId, createShapeElement, isImagePlaceholder } from "./ops";
import type { GroupElement, SceneDocument, SceneElement } from "./schema";

/**
 * Collage grids ("Collagen-Raster"): layouts of image placeholders that fill
 * a box (the page inside its margins, or a selected element). Cells are
 * given in grid units of a `columns` × `rows` raster; `collageCells` turns
 * them into boxes with an even gap between neighbours and none at the edges.
 */

export interface CollageLayout {
  id: string;
  label: string;
  columns: number;
  rows: number;
  /** [column, row, column span, row span] in grid units. */
  cells: readonly (readonly [number, number, number, number])[];
}

export const COLLAGE_LAYOUTS: readonly CollageLayout[] = [
  {
    id: "2-nebeneinander",
    label: "2 nebeneinander",
    columns: 2,
    rows: 1,
    cells: [
      [0, 0, 1, 1],
      [1, 0, 1, 1],
    ],
  },
  {
    id: "2-untereinander",
    label: "2 untereinander",
    columns: 1,
    rows: 2,
    cells: [
      [0, 0, 1, 1],
      [0, 1, 1, 1],
    ],
  },
  {
    id: "3-spalten",
    label: "3 Spalten",
    columns: 3,
    rows: 1,
    cells: [
      [0, 0, 1, 1],
      [1, 0, 1, 1],
      [2, 0, 1, 1],
    ],
  },
  {
    id: "3-zeilen",
    label: "3 Zeilen",
    columns: 1,
    rows: 3,
    cells: [
      [0, 0, 1, 1],
      [0, 1, 1, 1],
      [0, 2, 1, 1],
    ],
  },
  {
    id: "gross-links-2",
    label: "1 groß + 2 klein",
    columns: 3,
    rows: 2,
    cells: [
      [0, 0, 2, 2],
      [2, 0, 1, 1],
      [2, 1, 1, 1],
    ],
  },
  {
    id: "gross-oben-2",
    label: "1 groß oben + 2",
    columns: 2,
    rows: 3,
    cells: [
      [0, 0, 2, 2],
      [0, 2, 1, 1],
      [1, 2, 1, 1],
    ],
  },
  {
    id: "2x2",
    label: "2 × 2",
    columns: 2,
    rows: 2,
    cells: [
      [0, 0, 1, 1],
      [1, 0, 1, 1],
      [0, 1, 1, 1],
      [1, 1, 1, 1],
    ],
  },
  {
    id: "gross-oben-3",
    label: "1 groß + 3",
    columns: 3,
    rows: 3,
    cells: [
      [0, 0, 3, 2],
      [0, 2, 1, 1],
      [1, 2, 1, 1],
      [2, 2, 1, 1],
    ],
  },
  {
    id: "gross-links-3",
    label: "1 groß links + 3",
    columns: 3,
    rows: 3,
    cells: [
      [0, 0, 2, 3],
      [2, 0, 1, 1],
      [2, 1, 1, 1],
      [2, 2, 1, 1],
    ],
  },
  {
    id: "2-plus-3",
    label: "2 + 3",
    columns: 6,
    rows: 2,
    cells: [
      [0, 0, 3, 1],
      [3, 0, 3, 1],
      [0, 1, 2, 1],
      [2, 1, 2, 1],
      [4, 1, 2, 1],
    ],
  },
  {
    id: "3x3",
    label: "3 × 3",
    columns: 3,
    rows: 3,
    cells: Array.from(
      { length: 9 },
      (_, index) => [index % 3, Math.floor(index / 3), 1, 1] as const,
    ),
  },
  {
    id: "mosaik",
    label: "Mosaik",
    columns: 4,
    rows: 4,
    cells: [
      [0, 0, 2, 2],
      [2, 0, 2, 1],
      [2, 1, 1, 1],
      [3, 1, 1, 2],
      [0, 2, 1, 2],
      [1, 2, 2, 2],
      [3, 3, 1, 1],
    ],
  },
  {
    id: "streifen",
    label: "Streifen",
    columns: 4,
    rows: 1,
    cells: [
      [0, 0, 1, 1],
      [1, 0, 1, 1],
      [2, 0, 1, 1],
      [3, 0, 1, 1],
    ],
  },
];

export function getCollageLayout(id: string): CollageLayout | undefined {
  return COLLAGE_LAYOUTS.find((layout) => layout.id === id);
}

export interface CollageBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Boxes of the cells in `box`: neighbours are `gap` apart, the outer cells
 * touch the edges of the box. A gap larger than the box allows is reduced.
 */
export function collageCells(layout: CollageLayout, box: CollageBox, gap = 0): CollageBox[] {
  const maxGap = (length: number, count: number) =>
    count > 1 ? Math.max(0, (length - count) / (count - 1)) : 0;
  const gx = Math.max(0, Math.min(gap, maxGap(box.width, layout.columns)));
  const gy = Math.max(0, Math.min(gap, maxGap(box.height, layout.rows)));
  // Edge of grid line `index`: every line after the first adds one gap to the step.
  const edge = (start: number, length: number, count: number, g: number, index: number) =>
    start + (index / count) * (length + g);
  return layout.cells.map(([column, row, spanX, spanY]) => {
    const left = edge(box.x, box.width, layout.columns, gx, column);
    const top = edge(box.y, box.height, layout.rows, gy, row);
    const right = edge(box.x, box.width, layout.columns, gx, column + spanX) - gx;
    const bottom = edge(box.y, box.height, layout.rows, gy, row + spanY) - gy;
    return {
      x: round(left),
      y: round(top),
      width: Math.max(1, round(right - left)),
      height: Math.max(1, round(bottom - top)),
    };
  });
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Area a collage fills on a page: inside the layout grid's margins, else the whole page. */
export function collagePageBox(doc: Pick<SceneDocument, "format" | "layoutGrid">): CollageBox {
  const { width, height } = doc.format;
  if (doc.layoutGrid) {
    const { area } = layoutGridGeometry(doc.layoutGrid, width, height);
    if (area.width > 10 && area.height > 10) return area;
  }
  return { x: 0, y: 0, width, height };
}

export interface CollageOptions {
  /** Distance between cells (document px). */
  gap?: number;
  /** Corner radius of every cell (document px). */
  radius?: number;
  /** Form of every cell (scene/masks.ts). */
  mask?: ImageMask;
  fill?: string;
}

/** Default gap: 2 % of the shorter side of the box. */
export function defaultCollageGap(box: CollageBox): number {
  return Math.round(Math.min(box.width, box.height) * 0.02);
}

/** A group of image placeholders in the cells of a layout, ready to insert. */
export function buildCollage(
  layout: CollageLayout,
  box: CollageBox,
  options: CollageOptions = {},
): PlacedElements {
  const cells = collageCells(layout, box, options.gap ?? defaultCollageGap(box));
  const children: SceneElement[] = cells.map((cell, index) =>
    createShapeElement("rect", {
      x: round(cell.x - box.x),
      y: round(cell.y - box.y),
      width: cell.width,
      height: cell.height,
      name: `Bild ${index + 1}`,
      fill: options.fill ?? "#e4e4e7",
      radius: options.radius ?? 0,
      placeholder: "image",
      ...(options.mask ? { mask: { ...options.mask } } : {}),
    }),
  );
  const group: GroupElement = {
    id: createId("group"),
    type: "group",
    name: `Collage ${layout.label}`,
    x: box.x,
    y: box.y,
    width: Math.max(1, box.width),
    height: Math.max(1, box.height),
    rotation: 0,
    opacity: 1,
    locked: false,
    hidden: false,
    children: children.map((child) => child.id),
  };
  return { roots: [group], elements: [group, ...children] };
}

/**
 * Lays the cells of an existing collage group out again (other gap, radius,
 * form or layout with the same number of cells). Filled cells (images) keep
 * their picture; their crop is reset since the box changes. Returns false
 * when the group does not fit the layout.
 */
export function relayoutCollage(
  doc: SceneDocument,
  groupId: string,
  layout: CollageLayout,
  options: CollageOptions,
): boolean {
  const group = doc.elements[groupId];
  if (group?.type !== "group" || group.children.length !== layout.cells.length) return false;
  const box = { x: 0, y: 0, width: group.width, height: group.height };
  const cells = collageCells(layout, box, options.gap ?? defaultCollageGap(box));
  group.children.forEach((childId, index) => {
    const child = doc.elements[childId];
    const cell = cells[index]!;
    if (!child || (child.type !== "shape" && child.type !== "image")) return;
    const moved =
      child.x !== cell.x ||
      child.y !== cell.y ||
      child.width !== cell.width ||
      child.height !== cell.height;
    child.x = cell.x;
    child.y = cell.y;
    child.width = cell.width;
    child.height = cell.height;
    child.radius = options.radius ?? 0;
    if (options.mask) child.mask = { ...options.mask };
    else delete child.mask;
    if (child.type === "image" && moved) child.crop = null;
  });
  return true;
}

/**
 * The first empty cell (image placeholder) of a group, in order, also in
 * nested groups: pictures clicked in the media panel fill a selected
 * collage one after the other.
 */
export function nextEmptyCell(doc: SceneDocument, groupId: string): string | undefined {
  const group = doc.elements[groupId];
  if (group?.type !== "group" || group.locked) return undefined;
  for (const id of group.children) {
    const child = doc.elements[id];
    if (!child || child.locked || child.hidden) continue;
    if (isImagePlaceholder(child)) return id;
    if (child.type === "group") {
      const nested = nextEmptyCell(doc, id);
      if (nested) return nested;
    }
  }
  return undefined;
}
